import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

import { COLORS } from "../../../constants";

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}

function formatISODateToBR(iso: string): string {
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
}

function fileDateSuffix(): string {
  return `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}-${String(new Date().getDate()).padStart(2, "0")}`;
}

export type RelatorioPromocoesFiltroResumo = { label: string; value: string };

export interface RelatorioPromocoesPizzaExportInput {
  periodoInicioISO: string;
  periodoFimISO: string;
  filtros: RelatorioPromocoesFiltroResumo[];
  linhas: Array<{
    promocaoLabel: string;
    lojaNome: string;
    tamanhoNome: string;
    saborNome: string;
    quantidade: number;
  }>;
  totalQuantidadeFracao: number;
  geradoEmLabel: string;
}

function formatQuantidade(valor: number): string {
  return valor.toLocaleString("pt-BR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

type JsPdfWithAutoTable = jsPDF & {
  lastAutoTable?: { finalY: number };
};

export function exportRelatorioPromocoesPizzaXlsx(
  input: RelatorioPromocoesPizzaExportInput
): void {
  const pIni = formatISODateToBR(input.periodoInicioISO);
  const pFim = formatISODateToBR(input.periodoFimISO);
  const rows: (string | number)[][] = [
    ["Dom Belloni"],
    ["Relatório — Pizzas em promoções (integração)"],
    [],
    ["Período", `${pIni} a ${pFim}`],
    ...input.filtros.map((f) => [f.label, f.value]),
    [],
    ["Totais por promoção, loja, tamanho e sabor"],
    ["Promoção", "Loja", "Tamanho", "Sabor", "Quantidade"],
    ...input.linhas.map((r) => [
      r.promocaoLabel,
      r.lojaNome,
      r.tamanhoNome,
      r.saborNome,
      formatQuantidade(r.quantidade),
    ]),
    [],
    [
      "Total (soma frac.)",
      "",
      "",
      "",
      formatQuantidade(input.totalQuantidadeFracao),
    ],
    [],
    ["Gerado em", input.geradoEmLabel],
  ];
  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws["!cols"] = [
    { wch: 36 },
    { wch: 28 },
    { wch: 20 },
    { wch: 28 },
    { wch: 14 },
  ];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Relatório");
  XLSX.writeFile(wb, `relatorio-pizzas-promocoes-${fileDateSuffix()}.xlsx`);
}

export function exportRelatorioPromocoesPizzaPdf(
  input: RelatorioPromocoesPizzaExportInput
): void {
  const primary = hexToRgb(COLORS.PRIMARY);
  const primaryDark = hexToRgb(COLORS.PRIMARY_DARK);
  const pIni = formatISODateToBR(input.periodoInicioISO);
  const pFim = formatISODateToBR(input.periodoFimISO);

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 14;

  doc.setFillColor(primary[0], primary[1], primary[2]);
  doc.rect(0, 0, pageW, 28, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("Pizzas em promoções", margin, 12);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text("Dom Belloni · Pedidos integração", margin, 19);

  autoTable(doc, {
    startY: 32,
    body: [
      ["Período", `${pIni}  até  ${pFim}`],
      ...input.filtros.map((f) => [f.label, f.value]),
    ],
    theme: "plain",
    styles: {
      fontSize: 9,
      textColor: [42, 42, 42],
      cellPadding: 2.5,
    },
    columnStyles: {
      0: {
        fontStyle: "bold",
        cellWidth: 36,
      },
      1: { cellWidth: pageW - margin * 2 - 36 },
    },
    margin: { left: margin, right: margin },
  });

  const docExt = doc as JsPdfWithAutoTable;
  const afterMeta = (docExt.lastAutoTable?.finalY ?? 40) + 5;

  autoTable(doc, {
    startY: afterMeta,
    head: [["Promoção", "Loja", "Tamanho", "Sabor", "Qtde."]],
    body: input.linhas.map((r) => [
      r.promocaoLabel,
      r.lojaNome,
      r.tamanhoNome,
      r.saborNome,
      formatQuantidade(r.quantidade),
    ]),
    foot: [
      [
        "Total (soma frac.)",
        "",
        "",
        "",
        formatQuantidade(input.totalQuantidadeFracao),
      ],
    ],
    theme: "striped",
    headStyles: {
      fillColor: primary,
      textColor: 255,
      fontStyle: "bold",
      halign: "left",
      valign: "middle",
    },
    footStyles: {
      fillColor: primaryDark,
      textColor: 255,
      fontStyle: "bold",
    },
    columnStyles: {
      0: { cellWidth: 32, halign: "left" },
      1: { cellWidth: 24, halign: "left" },
      2: { cellWidth: 22, halign: "left" },
      3: { cellWidth: pageW - margin * 2 - 32 - 24 - 22 - 18, halign: "left" },
      4: { cellWidth: 18, halign: "right" },
    },
    bodyStyles: {
      textColor: [38, 38, 38],
      lineColor: [230, 230, 230],
    },
    alternateRowStyles: {
      fillColor: [255, 252, 251],
    },
    margin: { left: margin, right: margin, bottom: 16 },
    styles: {
      fontSize: 8,
      cellPadding: { top: 2.5, bottom: 2.5, left: 2, right: 2 },
      lineWidth: 0.08,
    },
  });

  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFontSize(7.5);
    doc.setTextColor(115, 115, 115);
    doc.setFont("helvetica", "normal");
    doc.text(
      `${input.geradoEmLabel}  ·  Folha ${i} de ${pages}`,
      margin,
      pageH - 6
    );
  }

  doc.save(`relatorio-pizzas-promocoes-${fileDateSuffix()}.pdf`);
}
